import mainStyle from "./mainStyle.module.scss";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { Grid, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import btn_news from "assets/images/icons/btn_help.svg";
import btn_news_light from "assets/images/icons/btn_help_light.svg";

import btn_announcement from "assets/images/icons/btn_announcement.svg";
import btn_announcement_light from "assets/images/icons/btn_announcement_light.svg";

import btn_resource_center from "assets/images/icons/btn_resource_center.svg";
import btn_resource_center_light from "assets/images/icons/btn_resource_center_light.svg";

import btn_remote_support from "assets/images/icons/btn_remote_support.svg";
import btn_remote_support_light from "assets/images/icons/btn_remote_support_light.png";

import icn_expandright from "assets/images/icons/icn_expandright.svg";

import useDataSourceManager from "hooks/useDataSourceManager";
import useAuthScope from "hooks/useAuthScope";
import { useUserContext } from "contexts/UserContext";

import News from "./News";
import Announcement from "./Announcement";
import RemoteSupport from "./RemoteSupport";

const Support = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const { auth, isAuthReady } = useAuthScope();
  const { userState } = useUserContext();

  const isSupporter =
    userState?.properties?.is_supporter === true ||
    userState?.properties?.is_supporter === "true";
  const isMspRoot = auth.realm?.hasManageOrg === true && !auth.orgs?.id;
  const canUseRemoteSupport = isAuthReady && !isSupporter && !isMspRoot;

  const [newsUuid] = useState(() => uuidv4());
  const [announcementUuid] = useState(() => uuidv4());

  const [newsList, setNewsList] = useState([]);
  const [announcementList, setAnnouncementList] = useState([]);
  const [remoteSupportList, setRemoteSupportList] = useState([]);

  const [nowAnchorId, setNowAnchorId] = useState(null);
  const [openParent, setOpenParent] = useState("news");

  const getNews = () => {
    sendRequest("/system/news.read", newsUuid);
  };

  const getAnnouncement = () => {
    sendRequest("/system/announcement.read", announcementUuid);
  };

  useEffect(() => {
    getNews();
    getAnnouncement();
  }, []);

  useEffect(() => {
    const newsResponse = getData(newsUuid);

    if (newsResponse?.data) {
      const sorted = [...(newsResponse.data || [])].sort(
        (a, b) =>
          new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      );

      setNewsList(sorted);
    }
  }, [dataMap, newsUuid, getData]);

  useEffect(() => {
    const announcementResponse = getData(announcementUuid);

    if (announcementResponse?.data) {
      const sorted = [...(announcementResponse.data || [])].sort(
        (a, b) =>
          new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      );

      setAnnouncementList(sorted);
    }
  }, [dataMap, announcementUuid, getData]);

  // Supporter and MSP-root users cannot open Remote Support.
  useEffect(() => {
    if (!canUseRemoteSupport && openParent === "remoteSupport") {
      setOpenParent("news");
      setNowAnchorId(null);

      navigate(location.pathname, {
        replace: true,
        state: {},
      });
    }
  }, [canUseRemoteSupport, openParent, navigate, location.pathname]);

  useEffect(() => {
    if (nowAnchorId !== null) {
      return;
    }

    if (openParent === "news" && newsList.length > 0) {
      setNowAnchorId(newsList[0].id);
      return;
    }

    if (openParent === "announcement" && announcementList.length > 0) {
      setNowAnchorId(announcementList[0].id);
      return;
    }

    if (
      canUseRemoteSupport &&
      openParent === "remoteSupport" &&
      remoteSupportList.length > 0
    ) {
      setNowAnchorId(remoteSupportList[0].id);
    }
  }, [
    openParent,
    newsList,
    announcementList,
    remoteSupportList,
    nowAnchorId,
    canUseRemoteSupport,
  ]);

  const handleSetNowAnchor = (id) => {
    setNowAnchorId(id);

    navigate(location.pathname, {
      replace: true,
      state: {},
    });
  };

  const handleParentClick = (parent) => {
    // Supporter 不可切換至 Remote Support
    if (!canUseRemoteSupport && parent === "remoteSupport") {
      return;
    }

    setOpenParent((previousParent) =>
      previousParent === parent ? null : parent,
    );

    setNowAnchorId(null);

    navigate(location.pathname, {
      replace: true,
      state: {},
    });
  };

  const handleResourceCenterClick = () => {
    window.open(
      "https://www.dlink.com/en/for-business/nuclias/nuclias-unity-Resource-Center",
      "_blank",
      "noopener,noreferrer",
    );
  };

  const selectedNews = useMemo(() => {
    return newsList.find((item) => item.id === nowAnchorId) || null;
  }, [newsList, nowAnchorId]);

  const selectedAnnouncement = useMemo(() => {
    return announcementList.find((item) => item.id === nowAnchorId) || null;
  }, [announcementList, nowAnchorId]);

  const selectedRemoteSupport = useMemo(() => {
    if (!canUseRemoteSupport) {
      return null;
    }

    return remoteSupportList.find((item) => item.id === nowAnchorId) || null;
  }, [remoteSupportList, nowAnchorId, canUseRemoteSupport]);

  const renderSubAnchorList = (list) => {
    return list.map((item) => (
      <div key={item.id}>
        <div
          className={mainStyle["subAnchorItem"]}
          style={{
            backgroundColor:
              nowAnchorId === item.id ? theme.palette.primary.subBlue : "",
            cursor: "pointer",
          }}
          onClick={() => handleSetNowAnchor(item.id)}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                minWidth: 0,
              }}
            >
              <span
                style={{
                  width: "6px",
                  height: "6px",
                  flexShrink: 0,
                  backgroundColor: theme.palette.primary.main,
                  margin: "2.5px 10px 2.5px 0",
                  borderRadius: "2px",
                  display: "inline-block",
                }}
              />

              <span
                className={mainStyle["subAnchorItem_text"]}
                style={{
                  color: theme.palette.primary.main,
                }}
                title={item.title}
              >
                {item.title}
              </span>
            </div>

            <img
              src={icn_expandright}
              alt=""
              style={{
                flexShrink: 0,
              }}
            />
          </div>
        </div>
      </div>
    ));
  };

  return (
    <div className={mainStyle["mainArea"]}>
      <div className={mainStyle["anchorArea"]}>
        <div className={mainStyle["anchorBody"]}>
          {/* What's News */}
          <Box>
            <div
              className={mainStyle["anchorItem"]}
              style={{
                backgroundColor:
                  openParent === "news" ? theme.palette.primary.main : "",
                cursor: "pointer",
              }}
              onClick={() => handleParentClick("news")}
            >
              <img
                src={openParent === "news" ? btn_news_light : btn_news}
                alt="news"
                style={{ width: "40px" }}
              />

              <span
                className={mainStyle["anchorItem_text"]}
                style={{
                  color:
                    openParent === "news"
                      ? theme.palette.primary.white
                      : theme.palette.primary.main,
                }}
              >
                What’s News
              </span>
            </div>

            {openParent === "news" && renderSubAnchorList(newsList)}
          </Box>

          {/* Announcement */}
          <Box>
            <div
              className={mainStyle["anchorItem"]}
              style={{
                backgroundColor:
                  openParent === "announcement"
                    ? theme.palette.primary.main
                    : "",
                cursor: "pointer",
              }}
              onClick={() => handleParentClick("announcement")}
            >
              <img
                src={
                  openParent === "announcement"
                    ? btn_announcement_light
                    : btn_announcement
                }
                alt="announcement"
                style={{ width: "40px" }}
              />

              <span
                className={mainStyle["anchorItem_text"]}
                style={{
                  color:
                    openParent === "announcement"
                      ? theme.palette.primary.white
                      : theme.palette.primary.main,
                }}
              >
                Announcement
              </span>
            </div>

            {openParent === "announcement" &&
              renderSubAnchorList(announcementList)}
          </Box>

          {/* isSupporter 為 false 才顯示 Remote Support */}
          {canUseRemoteSupport && (
            <Box>
              <div
                className={mainStyle["anchorItem"]}
                style={{
                  backgroundColor:
                    openParent === "remoteSupport"
                      ? theme.palette.primary.main
                      : "",
                  cursor: "pointer",
                }}
                onClick={() => handleParentClick("remoteSupport")}
              >
                <img
                  src={
                    openParent === "remoteSupport"
                      ? btn_remote_support_light
                      : btn_remote_support
                  }
                  alt="remoteSupport"
                  style={{ width: "40px" }}
                />
                <span
                  className={mainStyle["anchorItem_text"]}
                  style={{
                    color:
                      openParent === "remoteSupport"
                        ? theme.palette.primary.white
                        : theme.palette.primary.main,
                  }}
                >
                  Remote Support
                </span>
              </div>

              {openParent === "remoteSupport" &&
                renderSubAnchorList(remoteSupportList)}
            </Box>
          )}

          {/* Resource Center */}
          <Box>
            <div
              className={mainStyle["anchorItem"]}
              style={{
                cursor: "pointer",
              }}
              onClick={handleResourceCenterClick}
            >
              <img
                src={btn_resource_center}
                alt="resource"
                style={{ width: "40px" }}
                onMouseEnter={(event) => {
                  event.currentTarget.src = btn_resource_center_light;
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.src = btn_resource_center;
                }}
              />

              <span
                className={mainStyle["anchorItem_text"]}
                style={{
                  color: theme.palette.primary.main,
                }}
              >
                Resource Center
              </span>
            </div>
          </Box>
        </div>
      </div>

      <div className={mainStyle["userArea"]}>
        <Grid style={{ marginBottom: "20px" }}>
          {openParent === "news" && <News selected={selectedNews} />}

          {openParent === "announcement" && (
            <Announcement selected={selectedAnnouncement} />
          )}

          {canUseRemoteSupport && openParent === "remoteSupport" && (
            <RemoteSupport selected={selectedRemoteSupport} />
          )}
        </Grid>
      </div>
    </div>
  );
};

export default Support;
